import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, DollarSign, PiggyBank, TrendingUp, Car, CheckCircle, AlertTriangle, ArrowRight, HelpCircle } from 'lucide-react';

const Affordability = () => {
  const [income, setIncome] = useState(35000);
  const [expenses, setExpenses] = useState(15000);
  const [deposit, setDeposit] = useState(20000);
  const [term, setTerm] = useState(72);
  const [rate, setRate] = useState(11.75);
  const [includeInsurance, setIncludeInsurance] = useState(true);
  const [includeFuel, setIncludeFuel] = useState(true);

  useEffect(() => {
    document.title = 'What Car Can I Afford? | Affordability Calculator | AutoSearch SA';
  }, []);

  // Calculate affordable car price
  const calculateAffordability = () => {
    // Rule: Max 25-30% of gross income on transport (including fuel, insurance)
    const maxTransportBudget = income * 0.28;
    
    // Deduct estimated running costs
    const fuelEstimate = includeFuel ? 2500 : 0;
    const insuranceEstimate = includeInsurance ? 1200 : 0;
    const maintenanceEstimate = 500;
    
    const availableForInstalment = maxTransportBudget - fuelEstimate - insuranceEstimate - maintenanceEstimate;
    
    // Calculate max car price from instalment
    const monthlyRate = rate / 100 / 12;
    const n = term;
    
    if (availableForInstalment <= 0 || monthlyRate === 0) {
      return { maxPrice: 0, monthlyInstalment: 0, totalBudget: maxTransportBudget };
    }
    
    // PV = PMT × [(1 - (1 + r)^-n) / r]
    const maxFinance = availableForInstalment * ((1 - Math.pow(1 + monthlyRate, -n)) / monthlyRate);
    const maxPrice = maxFinance + deposit;
    
    return {
      maxPrice: Math.round(maxPrice / 1000) * 1000,
      monthlyInstalment: Math.round(availableForInstalment),
      totalBudget: Math.round(maxTransportBudget),
      fuelEstimate,
      insuranceEstimate,
      maintenanceEstimate
    };
  };

  const result = calculateAffordability();

  // Car suggestions based on budget
  const getCarSuggestions = (budget) => {
    if (budget < 100000) {
      return [
        { make: 'Suzuki', model: 'S-Presso', price: 'From R149,900' },
        { make: 'Renault', model: 'Kwid', price: 'From R159,900' },
        { make: 'Datsun', model: 'GO', price: 'Used from R80,000' }
      ];
    } else if (budget < 200000) {
      return [
        { make: 'Toyota', model: 'Starlet', price: 'From R199,900' },
        { make: 'Volkswagen', model: 'Polo Vivo', price: 'From R189,900' },
        { make: 'Suzuki', model: 'Swift', price: 'From R199,900' }
      ];
    } else if (budget < 350000) {
      return [
        { make: 'Toyota', model: 'Corolla Cross', price: 'From R349,900' },
        { make: 'Hyundai', model: 'Creta', price: 'From R329,900' },
        { make: 'Haval', model: 'Jolion', price: 'From R299,900' }
      ];
    } else if (budget < 500000) {
      return [
        { make: 'Toyota', model: 'Fortuner', price: 'From R499,900' },
        { make: 'Ford', model: 'Ranger', price: 'From R459,900' },
        { make: 'Volkswagen', model: 'Tiguan', price: 'From R459,900' }
      ];
    } else {
      return [
        { make: 'Toyota', model: 'Land Cruiser Prado', price: 'From R899,900' },
        { make: 'BMW', model: 'X3', price: 'From R749,900' },
        { make: 'Mercedes-Benz', model: 'GLC', price: 'From R789,900' }
      ];
    }
  };

  const suggestions = getCarSuggestions(result.maxPrice);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero */}
      <section className="bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800 text-white py-12">
        <div className="container">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <PiggyBank className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold">What Car Can I Afford?</h1>
                <p className="text-indigo-200">Smart budgeting for your next vehicle</p>
              </div>
            </div>
            <p className="text-lg text-indigo-100 mt-4">
              Use our affordability calculator to find out how much car you can realistically afford based on your income and expenses.
            </p>
          </div>
        </div>
      </section>

      <div className="container py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calculator */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-indigo-600" />
                Your Financial Details
              </h2>

              <div className="space-y-6">
                {/* Income */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Gross Monthly Income
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                    <input
                      type="number"
                      value={income}
                      onChange={(e) => setIncome(Number(e.target.value))}
                      className="w-full pl-10 pr-4 py-4 text-lg border border-gray-200 dark:border-gray-700 rounded-xl dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <input
                    type="range"
                    min="10000"
                    max="150000"
                    step="5000"
                    value={income}
                    onChange={(e) => setIncome(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>R10,000</span>
                    <span>R150,000</span>
                  </div>
                </div>

                {/* Deposit */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Deposit Available
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                    <input
                      type="number"
                      value={deposit}
                      onChange={(e) => setDeposit(Number(e.target.value))}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                {/* Term & Rate */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Finance Term
                    </label>
                    <select
                      value={term}
                      onChange={(e) => setTerm(Number(e.target.value))}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                    >
                      <option value={36}>36 months</option>
                      <option value={48}>48 months</option>
                      <option value={60}>60 months</option>
                      <option value={72}>72 months</option>
                      <option value={84}>84 months</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Interest Rate (%)
                    </label>
                    <input
                      type="number"
                      step="0.25"
                      value={rate}
                      onChange={(e) => setRate(Number(e.target.value))}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                {/* Running Costs */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Include Running Costs
                  </label>
                  <div className="flex flex-wrap gap-3">
                    <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition ${includeInsurance ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30' : 'border-gray-200 dark:border-gray-700'}`}>
                      <input
                        type="checkbox"
                        checked={includeInsurance}
                        onChange={(e) => setIncludeInsurance(e.target.checked)}
                        className="rounded text-indigo-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Insurance (~R1,200/m)</span>
                    </label>
                    <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition ${includeFuel ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30' : 'border-gray-200 dark:border-gray-700'}`}>
                      <input
                        type="checkbox"
                        checked={includeFuel}
                        onChange={(e) => setIncludeFuel(e.target.checked)}
                        className="rounded text-indigo-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Fuel (~R2,500/m)</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Car Suggestions */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Car className="w-5 h-5 text-indigo-600" />
                Cars In Your Budget
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {suggestions.map((car, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 text-center">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{car.make} {car.model}</h3>
                    <p className="text-sm text-indigo-600 dark:text-indigo-400">{car.price}</p>
                  </div>
                ))}
              </div>
              <Link
                to="/"
                className="mt-4 w-full py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition flex items-center justify-center gap-2"
              >
                Search Cars In Budget
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* Results Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-6 text-white">
              <h3 className="text-lg font-bold mb-4">Your Affordability</h3>
              
              <div className="bg-white/10 rounded-xl p-4 mb-4">
                <p className="text-indigo-200 text-sm">Maximum Car Price</p>
                <p className="text-4xl font-bold">R{result.maxPrice.toLocaleString()}</p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-indigo-200">Monthly Budget</span>
                  <span className="font-semibold">R{result.totalBudget.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-indigo-200">Max Instalment</span>
                  <span className="font-semibold">R{result.monthlyInstalment.toLocaleString()}</span>
                </div>
                {includeInsurance && (
                  <div className="flex justify-between text-sm">
                    <span className="text-indigo-200">Est. Insurance</span>
                    <span>R{result.insuranceEstimate.toLocaleString()}</span>
                  </div>
                )}
                {includeFuel && (
                  <div className="flex justify-between text-sm">
                    <span className="text-indigo-200">Est. Fuel</span>
                    <span>R{result.fuelEstimate.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-indigo-200">Est. Maintenance</span>
                  <span>R{result.maintenanceEstimate.toLocaleString()}</span>
                </div>
              </div>

              <div className="border-t border-white/20 pt-4">
                <div className="flex items-start gap-2 text-sm">
                  <HelpCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-indigo-200">
                    Based on the 28% rule: No more than 28% of gross income should go to transport costs.
                  </p>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mt-6">
              <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Smart Buying Tips
              </h4>
              <ul className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
                <li>• A bigger deposit = lower instalments</li>
                <li>• Shorter term = less interest paid</li>
                <li>• Include ALL costs when budgeting</li>
                <li>• Get pre-approved before shopping</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Affordability;
